import {
  FDR_FILTER_STATUSES,
  FDR_PROGRAM_FILTERS,
  FDRStatus,
  FDRStatusQuery,
  ProgramModel,
} from '@cibo/core'
import { Autocomplete, FormControl, FormLabel, Skeleton, TextField } from '@mui/material'
import { useField } from 'formik'
import { equals, uniq } from 'ramda'
import { SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const ProgramStatusFilter = ({ programs }: { programs?: ProgramModel[] }) => {
  const { t } = useTranslation('@cibo/landmanager/ProgramStatusFilter')
  const fieldContext = useField('fdrStatusAnyOf')
  const { value = [] } = fieldContext[1]
  const { setValue } = fieldContext[2]
  const [programsValue, setProgramsValue] = useState<string[]>([])
  const [statusValue, setStatusValue] = useState<string[]>([])

  useEffect(() => {
    const fieldPrograms = programs
      ? (uniq(
          value
            .map((value: FDRStatusQuery) => {
              return typeof value === 'object' && value.programId ? value.programId : undefined
            })
            .filter(Boolean)
        ).filter(Boolean) as string[])
      : []
    setProgramsValue(fieldPrograms)

    const statuses = uniq(
      value
        .map((value: FDRStatusQuery) => {
          return typeof value === 'object' && value.status ? value.status : value
        })
        .filter(Boolean) as string[]
    )
    setStatusValue(equals(statuses.sort(), FDR_PROGRAM_FILTERS) ? [] : statuses)
  }, [fieldContext[1].value, programs])

  const handleStatusChange = (_: SyntheticEvent<Element, Event>, value: string[]) => {
    const FDRFilters = !!programsValue.length
      ? uniq(
          programsValue
            .map(programId => {
              return (value.length ? value : FDR_PROGRAM_FILTERS).map(status => ({
                programId,
                status,
              }))
            })
            .flat()
        )
      : value.map(status => ({ status }))
    setValue(equals(uniq(value).sort(), FDR_PROGRAM_FILTERS) ? [] : FDRFilters)
  }

  const handleProgramChange = (_: SyntheticEvent<Element, Event>, value: string[]) => {
    const activeStatuses = !!statusValue.length ? statusValue : FDR_PROGRAM_FILTERS
    const FDRFilters = uniq(
      activeStatuses
        .map(status => {
          return value.map(programId => ({ programId, status }))
        })
        .flat()
    )
    setValue(FDRFilters)
  }

  const programsSorted = useMemo(() => {
    const programsByStatus = {
      openEnrollment: programs && programs.filter(program => program.isOpenForEnrollment),
      futureOpportunities: programs && programs.filter(program => program.isFuture),
      closedEnrollment: programs && programs.filter(program => program.isNotOpenFutureOrClosed),
      completed: programs && programs.filter(program => program.isClosed),
    }
    return Object.values(programsByStatus).flat().filter(Boolean) as ProgramModel[]
  }, [programs])

  if (!programs) {
    return (
      <FormControl>
        <FormLabel>
          <span style={{ opacity: 0.5 }}>{t('program')}</span>
        </FormLabel>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
        <FormLabel>
          <span style={{ opacity: 0.5 }}>{t('status')}</span>
        </FormLabel>
        <Skeleton variant="rectangular" height={40} />
      </FormControl>
    )
  }

  return (
    <>
      {programs && (
        <FormControl>
          <FormLabel>{t('program')}</FormLabel>
          <Autocomplete<string, true, false, false>
            multiple
            limitTags={2}
            options={programsSorted.map(program => program.programId)}
            getOptionLabel={programId => {
              const program = programsSorted.find(p => p.programId === programId)
              return program ? program.title : ''
            }}
            renderInput={params => (
              <TextField
                {...params}
                placeholder={programsValue.length === 0 ? t('allPrograms') : ''}
              />
            )}
            groupBy={c =>
              t(`programStatus_${programs.find(p => p.programId === c)?.displayStatus}`)
            }
            onChange={handleProgramChange}
            size="small"
            value={programsValue}
            fullWidth
          />
        </FormControl>
      )}
      <FormControl>
        <FormLabel>{t('status')}</FormLabel>
        <Autocomplete<FDRStatus, true, false, false>
          multiple
          limitTags={2}
          options={FDR_FILTER_STATUSES as unknown as FDRStatus[]}
          getOptionLabel={status => t(`status_${status}`)}
          renderInput={params => (
            <TextField {...params} placeholder={statusValue.length === 0 ? t('allStatuses') : ''} />
          )}
          onChange={handleStatusChange}
          size="small"
          value={statusValue as FDRStatus[]}
          fullWidth
        />
      </FormControl>
    </>
  )
}
